import { useCallback, useMemo } from "react";
import { useAtomValue } from "jotai";
import { PageType } from "../config/page.types";
import { UserPermissionDTO } from "../datasources/api-client/data-contracts";
import { ApiRoutes } from "../datasources/networking/apiRoutes";
import { currentUser } from "../state/auth";
import { TransportPerspective } from "../state/preferences";
import { FeatureKey, HttpMethod } from "./apiPermissions";
import { canAccessPage, canCall, hasFeature, toPermissionsList } from "./permissionUtils";

/**
 * Exposes the current user permissions and the functions to check them.
 * Components must use this hook instead of reading the stored user data.
 */
export const usePermissions = () => {
	const userData = useAtomValue(currentUser);

	// Current user permissions as a plain list; recomputed only when the user permissions change.
	const permissions = useMemo<UserPermissionDTO[]>(
		() => toPermissionsList(userData?.permissions),
		[userData?.permissions]
	);

	// Checks a single feature (optionally its perspective variant).
	// Meant for elements not bound to an API call; not used yet.
	const checkFeature = useCallback(
		(feature: FeatureKey, perspective?: TransportPerspective) => hasFeature(permissions, feature, perspective),
		[permissions]
	);

	// Checks a single API call.
	// Used by useReadResource / useWriteResource to compute isForbidden.
	const checkCall = useCallback(
		(route: ApiRoutes, method: HttpMethod, perspective?: TransportPerspective) =>
			canCall(permissions, route, method, perspective),
		[permissions]
	);

	// Checks if a page is accessible.
	// Used by the side menu, Protected (route guard) and the buttons that open a page.
	const checkPageAccess = useCallback(
		(pageType: PageType, perspective?: TransportPerspective) => canAccessPage(permissions, pageType, perspective),
		[permissions]
	);

	return {
		permissions,
		hasFeature: checkFeature,
		canCall: checkCall,
		canAccessPage: checkPageAccess,
	};
};
